import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { Loader2 } from "lucide-react";

import { cn } from "../lib/utils";

const buttonVariants = cva(
  // Atlas restyle (09.3-05): base weight font-semibold (600) — primary bumps to 700
  // via the default variant; radius is the C1 asymmetric-corner pair (rounded-button
  // = 12px 4px 12px 4px), never the legacy rounded-md. Elevation is hairlines-over-
  // shadows (C2): NO light-mode box-shadows; the dark accent glow lives on primary.
  // State contract (UI-SPEC §Component States, D-09/D-18): every variant exposes
  // hover (subtle token shift), focus-visible (--ring), active/pressed (distinct
  // from hover), disabled (reduced-emphasis + not focusable). transition-all is
  // ~150ms (duration-150) and gated by motion-reduce:transition-none so it honors
  // prefers-reduced-motion: reduce. All state colors are token-driven (no literals).
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-sm font-semibold transition-all duration-150 motion-reduce:transition-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Primary: Terracotta accent fill, weight 700, plus a dark-only accent glow
        // (C2). The glow color is derived from the --primary token via color-mix (0.3
        // alpha) — token-driven, no literal color (plan 01 shipped no glow token).
        default:
          "bg-primary text-primary-foreground font-bold hover:bg-primary/90 active:bg-primary/80 dark:shadow-[0_0_18px_color-mix(in_oklch,var(--primary)_30%,transparent)]",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 active:bg-destructive/80 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        // Secondary/outline: hairline border (C2), weight 600 — no elevation shadow.
        outline:
          "border bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent/80 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 dark:active:bg-input/70",
        secondary:
          "border bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70",
        ghost:
          "hover:bg-accent hover:text-accent-foreground active:bg-accent/80 dark:hover:bg-accent/50 dark:active:bg-accent/40",
        link: "text-primary underline-offset-4 hover:underline active:text-primary/80",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  pending = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /**
     * Pending (in-flight) state — the canonical Layer-1 idempotency guard for
     * every mutating action (UI-SPEC §SSR, D-10/D-11). Maps to a caller's React 19
     * `useTransition`/`useActionState` `isPending`. When true the button is
     * `disabled` (so a second click is suppressed) and renders a leading spinner.
     * Not applicable when `asChild` is set (the child owns its content).
     */
    pending?: boolean;
  }) {
  const isDisabled = disabled || pending;

  // With asChild, Radix Slot REQUIRES a single React element child — it cannot
  // receive the spinner + children array, so the pending affordance (a
  // button-only concern) is omitted and the caller's child is passed untouched.
  if (asChild) {
    return (
      <Slot.Root
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {children}
      </Slot.Root>
    );
  }

  return (
    <button
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-pending={pending || undefined}
      aria-busy={pending || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={isDisabled}
      {...props}
    >
      {pending && (
        <Loader2
          data-slot="button-spinner"
          className="animate-spin"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  );
}

export { Button, buttonVariants };
